/**
 * Analytics consent — the one switch GA4 and PostHog both obey.
 *
 * Nothing that stores an analytics identifier on the player's device, or sends usage events,
 * runs before the player says yes (EU ePrivacy / GDPR). Until a choice is made, and after
 * "Only essential":
 *  - GA4 runs under Consent Mode v2 with every storage type DENIED (set in index.html before the
 *    tag loads) and `trackEvent` sends it nothing — so no cookie and no hit.
 *  - PostHog starts opted OUT, in memory-only persistence, with session recording disabled.
 * "Accept" grants `analytics_storage` (ad storage stays denied — Suikamon shows no ads), opts
 * PostHog in with normal persistence and lets it record sessions if the project enables it.
 *
 * The choice lives in localStorage. index.html reads the same key before gtag loads so a
 * returning player who accepted is counted from their first hit. Change the key in both places.
 */

/** localStorage key — also read by the inline consent script in index.html. */
export const ANALYTICS_CONSENT_KEY = "suikamon.analytics_consent.v1";
/** Window event fired when the choice changes, so the analytics layer can re-apply it. */
export const ANALYTICS_CONSENT_EVENT = "suikamon:analytics-consent";

export type AnalyticsConsent = "granted" | "denied";

export function getAnalyticsConsent(): AnalyticsConsent | null {
  try {
    const value = window.localStorage.getItem(ANALYTICS_CONSENT_KEY);
    return value === "granted" || value === "denied" ? value : null;
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent(): boolean {
  return getAnalyticsConsent() === "granted";
}

export function setAnalyticsConsent(choice: AnalyticsConsent): void {
  try {
    window.localStorage.setItem(ANALYTICS_CONSENT_KEY, choice);
  } catch {
    // Storage blocked: the choice holds for this page view only; the bar asks again next visit.
  }
  window.dispatchEvent(new CustomEvent<AnalyticsConsent>(ANALYTICS_CONSENT_EVENT, { detail: choice }));
}

/** Forget the choice so the consent bar asks again ("Cookie settings" in the footer). */
export function resetAnalyticsConsent(): void {
  try {
    window.localStorage.removeItem(ANALYTICS_CONSENT_KEY);
  } catch {
    // Nothing stored to forget.
  }
  window.dispatchEvent(new CustomEvent<null>(ANALYTICS_CONSENT_EVENT, { detail: null }));
}
